import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, User, Mail, Edit, Trash2, Phone, Import as Passport, Copy, ExternalLink, Search, UserCheck, AlertCircle, FileImage, Upload } from 'lucide-react';
import { useGuidesStore } from '../../stores/guidesStore';
import { Guide } from '../../types/guide';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { AddGuideModal } from './AddGuideModal';
import { EditGuideInfoModal } from './EditGuideInfoModal';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { firestore } from '../../lib/firebase/config';
import { uploadFile } from '../../utils/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

export const OrganizerGuidesList = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [completionFilter, setCompletionFilter] = useState<'all' | 'complete' | 'incomplete'>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    guide: Guide | null;
  }>({
    isOpen: false,
    guide: null,
  });
  const [isUploading, setIsUploading] = useState<string | null>(null);
  const [photoDeleteConfirm, setPhotoDeleteConfirm] = useState<{
    isOpen: boolean;
    guideId: string | null;
  }>({
    isOpen: false,
    guideId: null,
  });

  const {
    guides,
    loading,
    error,
    initialized,
    initializeListener,
    deleteGuide,
    updateGuide,
  } = useGuidesStore();
  const organizerId = localStorage.getItem('userId');
  const { translations } = useLanguage();

  // Initialize guides data
  useEffect(() => {
    if (!initialized) {
      initializeListener();
    }
  }, [initialized, initializeListener]);

  const handleDelete = async (guide: Guide) => {
    try {
      // Check if this guide has any linked pilgrims
      const linksRef = collection(firestore, 'PilgrimGuideLinks');
      const linksQuery = query(
        linksRef, 
        where('guideId', '==', guide.id),
        where('status', '==', 'approved')
      );
      
      const linksSnapshot = await getDocs(linksQuery);
      
      // If there are any approved links, prevent deletion
      if (!linksSnapshot.empty) {
        toast.error(
          translations?.guides?.cannotDeleteWithPilgrims || 
          `Cannot delete guide ${guide.name} because they have ${linksSnapshot.size} pilgrim(s) linked to them. Please remove all linked pilgrims first.`
        );
        return;
      }
      
      await deleteGuide(guide.id);
      toast.success(
        translations?.messages?.deleteSuccess || 'Guide deleted successfully.'
      );
    } catch (error) {
      console.error('Error deleting guide:', error);
      toast.error(
        translations?.messages?.deleteError || 'Failed to delete guide.'
      );
    }
    setDeleteConfirm({ isOpen: false, guide: null });
  };

  const handleCopyText = (text: string, type: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast.success(`${type} copied to clipboard`);
      },
      (err) => {
        toast.error('Could not copy text');
      }
    );
  };

  const handlePassportPhotoUpload = async (guideId: string, file: File) => {
    try {
      // Get guide data to use passport number for filename
      const guide = guides[guideId];
      
      // Validate that passport number exists
      if (!guide.passportNumber) {
        toast.error('Passport number is required before uploading passport photo');
        return;
      }
      
      setIsUploading(guideId);
      
      // Create a new file with passport number as name but keep original extension
      const fileExtension = file.name.split('.').pop();
      const renamedFile = new File(
        [file],
        `passport_${guide.passportNumber}.${fileExtension}`,
        { type: file.type }
      );
      
      const result = await uploadFile(renamedFile);
      await updateGuide(guideId, {
        passportPhotoUrl: result.url,
        updatedAt: Date.now()
      });
      toast.success('Passport photo uploaded successfully');
    } catch (error) {
      console.error('Error uploading passport photo:', error);
      toast.error('Failed to upload passport photo');
    } finally {
      setIsUploading(null);
    }
  };

  const handleDeletePassportPhoto = async (guideId: string) => {
    try {
      setIsUploading(guideId);
      await updateGuide(guideId, {
        passportPhotoUrl: null,
        updatedAt: Date.now()
      });
      toast.success('Passport photo deleted successfully');
    } catch (error) {
      console.error('Error deleting passport photo:', error);
      toast.error('Failed to delete passport photo');
    } finally {
      setIsUploading(null);
      setPhotoDeleteConfirm({ isOpen: false, guideId: null });
    }
  };

  const confirmDeletePhoto = (guideId: string) => {
    setPhotoDeleteConfirm({
      isOpen: true,
      guideId
    });
  };

  // Check if guide has complete profile
  // Now includes passport photo as a requirement
  const isGuideComplete = (guide: Guide): boolean => {
    return !!(guide.passportNumber && guide.nusukEmail && guide.mainPhone && guide.passportPhotoUrl);
  };

  // Filter guides based on search term and completion status
  const filteredGuides = React.useMemo(() => {
    if (!organizerId) return [];
    
    return Object.values(guides)
      .filter(
        (guide) => guide.organizerId === organizerId && (
          searchTerm === '' || 
          guide.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
          guide.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
          (guide.passportNumber && guide.passportNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (guide.mainPhone && guide.mainPhone.includes(searchTerm)) ||
          (guide.hajjPhone && guide.hajjPhone.includes(searchTerm))
        )
      )
      .filter(guide => {
        if (completionFilter === 'all') return true;
        const isComplete = isGuideComplete(guide);
        return completionFilter === 'complete' ? isComplete : !isComplete;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [guides, organizerId, searchTerm, completionFilter]);

  // Calculate completion stats
  const stats = React.useMemo(() => {
    if (!organizerId) return { total: 0, complete: 0, incomplete: 0, percentage: 0 };
    
    const orgGuides = Object.values(guides).filter(guide => guide.organizerId === organizerId);
    const complete = orgGuides.filter(isGuideComplete).length;
    
    return {
      total: orgGuides.length,
      complete,
      incomplete: orgGuides.length - complete,
      percentage: orgGuides.length > 0 ? Math.round((complete / orgGuides.length) * 100) : 0
    };
  }, [guides, organizerId]);

  // Helper function to extract passport number from URL
  const getFilenameFromUrl = (url: string) => {
    try {
      // Extract filename from path
      const pathSegments = new URL(url).pathname.split('/');
      const filename = pathSegments[pathSegments.length - 1];
      
      // Try to extract passport number from the filename
      // Pattern: passport_PASSPORTNUMBER.extension
      const match = filename.match(/passport_([^.]+)\.(.+)$/);
      if (match && match.length >= 3) {
        return `${match[1]}.${match[2]}`;
      }
      
      // If pattern doesn't match, just return the filename
      return filename;
    } catch (error) {
      return 'passport-photo';
    }
  };

  if (loading && !initialized) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <>
      {/* Header section with stats */}
      <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              My Guides ({stats.total})
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage your guide roster and their contact information
            </p>
          </div>
          
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors self-start"
          >
            <Plus size={20} />
            <span>{translations?.guidesList?.addButton || 'Add Guide'}</span>
          </button>
        </div>
        
        {/* Stats cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-primary-50 p-4 rounded-lg">
            <div className="text-sm text-primary-600 mb-1">Total Guides</div>
            <div className="text-2xl font-bold text-primary-700">{stats.total}</div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-green-600 mb-1">Complete Profiles</div>
            <div className="text-2xl font-bold text-green-700">{stats.complete}</div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-sm text-yellow-600 mb-1">Incomplete Profiles</div>
            <div className="text-2xl font-bold text-yellow-700">{stats.incomplete}</div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Profile Completion</span>
            <span className="text-sm font-medium text-gray-700">{stats.percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="h-2.5 rounded-full bg-primary-500"
              style={{ width: `${stats.percentage}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Search and filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </div>
          
          {/* Completion filter */}
          <div>
            <select
              value={completionFilter}
              onChange={(e) => setCompletionFilter(e.target.value as 'all' | 'complete' | 'incomplete')}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Guides</option>
              <option value="complete">Complete Profiles</option>
              <option value="incomplete">Incomplete Profiles</option>
            </select>
          </div>
        </div>
      </div>

      {/* Guides grid */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGuides.length > 0 ? (
            filteredGuides.map((guide) => (
              <motion.div
                key={guide.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative"
              >
                {/* Status Badge - Top Right Corner */}
                <div className="absolute top-3 right-3">
                  {isGuideComplete(guide) ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <UserCheck size={10} />
                      <span>Complete</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <AlertCircle size={10} />
                      <span>Incomplete</span>
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-primary-50 rounded-full flex-shrink-0">
                    <User className="text-primary-500" size={20} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-lg truncate pr-20">{guide.name}</h3>
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Mail size={14} />
                      <span className="truncate">{guide.email}</span>
                    </div>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="space-y-3 mb-4">
                  {guide.passportNumber && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-1.5 bg-gray-100 rounded-full flex-shrink-0">
                        <Passport size={14} className="text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0 flex justify-between items-center">
                        <span className="text-gray-600 truncate mr-2">{guide.passportNumber}</span>
                        <button
                          onClick={() => handleCopyText(guide.passportNumber!, 'Passport')}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  )}

                  {guide.nusukEmail && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-1.5 bg-gray-100 rounded-full flex-shrink-0">
                        <ExternalLink size={14} className="text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0 flex justify-between items-center">
                        <span className="text-gray-600 truncate mr-2">{guide.nusukEmail}</span>
                        <button
                          onClick={() => handleCopyText(guide.nusukEmail!, 'Nusuk Email')}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  )}

                  {guide.mainPhone && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-1.5 bg-gray-100 rounded-full flex-shrink-0">
                        <Phone size={14} className="text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0 flex justify-between items-center">
                        <span className="text-gray-600 truncate mr-2">{guide.mainPhone}</span>
                        <button
                          onClick={() => handleCopyText(guide.mainPhone!, 'Phone')}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  )}

                  {guide.hajjPhone && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-1.5 bg-gray-100 rounded-full flex-shrink-0">
                        <Phone size={14} className="text-green-500" />
                      </div>
                      <div className="flex-1 min-w-0 flex justify-between items-center">
                        <span className="text-gray-600 truncate mr-2">{guide.hajjPhone}</span>
                        <button
                          onClick={() => handleCopyText(guide.hajjPhone!, 'Hajj Phone')}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Passport Photo Section */}
                  <div className="flex items-center gap-3 text-sm">
                    <div className="p-1.5 bg-gray-100 rounded-full flex-shrink-0">
                      <FileImage size={14} className="text-purple-500" />
                    </div>
                    <div className="flex-1 min-w-0 flex justify-between items-center">
                      <span className="text-gray-600 truncate mr-2">
                        {guide.passportPhotoUrl ? getFilenameFromUrl(guide.passportPhotoUrl) : 'No Passport Photo'}
                      </span>
                      <div className="flex items-center">
                        {guide.passportPhotoUrl ? (
                          <>
                            <a 
                              href={guide.passportPhotoUrl} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="p-1 text-primary-500 hover:text-primary-600"
                              title="View Passport Photo"
                            >
                              <ExternalLink size={14} />
                            </a>
                            <button
                              onClick={() => confirmDeletePhoto(guide.id)}
                              className="p-1 text-red-400 hover:text-red-600 ml-1"
                              title="Delete Passport Photo"
                              disabled={isUploading === guide.id}
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            <input
                              type="file"
                              id={`passport-upload-${guide.id}`}
                              className="sr-only"
                              accept="image/jpeg,image/png,application/pdf"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handlePassportPhotoUpload(guide.id, e.target.files[0]);
                                }
                              }}
                              disabled={!guide.passportNumber} // Disable if no passport number
                            />
                            <label 
                              htmlFor={`passport-upload-${guide.id}`} 
                              className={`cursor-pointer ${!guide.passportNumber ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={!guide.passportNumber ? 'Passport number required before uploading photo' : 'Upload passport photo'}
                            >
                              {isUploading === guide.id ? (
                                <div className="p-1 text-primary-500">
                                  <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                              ) : (
                                <div className="p-1 text-gray-400 hover:text-gray-600">
                                  <Upload size={14} />
                                </div>
                              )}
                            </label>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {!guide.passportNumber && !guide.nusukEmail && !guide.mainPhone && !guide.hajjPhone && !guide.passportPhotoUrl && (
                    <div className="py-2 px-4 bg-gray-50 rounded-lg text-sm text-gray-500 text-center">
                      No additional contact details provided
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center pt-3 border-t">
                  <button
                    onClick={() => {
                      setSelectedGuide(guide);
                      setIsEditModalOpen(true);
                    }}
                    className="flex items-center gap-1.5 text-primary-600 bg-primary-50 px-3 py-1.5 rounded-lg hover:bg-primary-100 transition-colors"
                  >
                    <Edit size={14} />
                    <span>Edit</span>
                  </button>
                  
                  <button
                    onClick={() => setDeleteConfirm({ isOpen: true, guide })}
                    className="flex items-center gap-1.5 text-red-600 bg-red-50 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Trash2 size={14} />
                    <span>Delete</span>
                  </button>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="flex flex-col items-center">
                <div className="p-3 bg-gray-100 rounded-full mb-3">
                  <User className="text-gray-400" size={24} />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  {searchTerm || completionFilter !== 'all' 
                    ? 'No guides match your search' 
                    : 'No guides yet'}
                </h3>
                <p className="text-gray-500 mb-4 max-w-md">
                  {searchTerm || completionFilter !== 'all'
                    ? 'Try adjusting your search or filters to find what you\'re looking for.'
                    : 'Get started by adding your first guide to your organization.'}
                </p>
                {(searchTerm || completionFilter !== 'all') ? (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setCompletionFilter('all');
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Clear Filters
                  </button>
                ) : (
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                  >
                    <Plus size={18} />
                    <span>Add First Guide</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Guide Modal */}
      <AddGuideModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />

      {/* Edit Guide Modal */}
      {selectedGuide && (
        <EditGuideInfoModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedGuide(null);
          }}
          guide={selectedGuide}
          onUpdate={updateGuide}
        />
      )}

      {/* Delete Guide Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, guide: null })}
        onConfirm={() =>
          deleteConfirm.guide && handleDelete(deleteConfirm.guide)
        }
        title={translations?.guidesList?.deleteTitle || 'Delete Guide'}
        message={
          translations?.guidesList?.deleteMessage?.replace(
            '{name}',
            deleteConfirm.guide?.name || ''
          ) ||
          `Are you sure you want to delete the guide "${deleteConfirm.guide?.name}"?`
        }
        type="danger"
        confirmText={translations?.common?.delete || 'Delete'}
        cancelText={translations?.common?.cancel || 'Cancel'}
      />

      {/* Delete Photo Confirmation Dialog */}
      <ConfirmDialog
        isOpen={photoDeleteConfirm.isOpen}
        onClose={() => setPhotoDeleteConfirm({ isOpen: false, guideId: null })}
        onConfirm={() => photoDeleteConfirm.guideId && handleDeletePassportPhoto(photoDeleteConfirm.guideId)}
        title={'Delete Passport Photo'}
        message={'Are you sure you want to delete this passport photo? This action cannot be undone.'}
        type="danger"
        confirmText={'Delete'}
        cancelText={'Cancel'}
      />
    </>
  );
};
